import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";

const Heatmap = ({
  data,
  rowNames,
  columnNames,
  height = 400,
  fontSize = 12,
  fontColor = "black",
  width = "100%",
  baseColor = "#E49492",
  showValues = true,
  colorScaleName = "interpolateBlues",
  legendPosition = "right",
  gapBetweenCells = false,
  showXAxisLine = false,
  showYAxisLine = false,
  showStroke = true,
  overflow = "auto"
}) => {
  const ref = useRef();

  useEffect(() => {
    const margin = { top: 50, right: 100, bottom: 100, left: 100 };
    const containerWidth = ref.current.offsetWidth;
    const svgWidth = width === "100%" ? containerWidth : parseInt(width, 10);
    const svgHeight = height + margin.top + margin.bottom;

    // Clear previous SVG content
    d3.select(ref.current).selectAll("*").remove();

    // Create SVG container
    const svg = d3
      .select(ref.current)
      .append("svg")
      .attr("width", svgWidth)
      .attr("height", svgHeight)
      .append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`);

    const innerWidth = svgWidth - margin.left - margin.right;
    const innerHeight = height;

    // Scales
    const x = d3
      .scaleBand()
      .range([0, innerWidth])
      .domain(columnNames)
      .padding(gapBetweenCells ? 0.05 : 0.2);
    // .padding(gapBetweenCells ? 0.05 : 0.2);
    // .padding(0.4);

    const y = d3
      .scaleBand()
      .range([0, innerHeight])
      .domain(rowNames)
    //   .padding(gapBetweenCells ? 0.05 : 0);
    .padding(gapBetweenCells ? 0.05 : 0.2);

    // const colorScale = d3
    //   .scaleSequential(d3[colorScaleName])
    //   .domain([d3.min(data.flat()), d3.max(data.flat())]);

      // Custom color scale based on baseColor
    const colorScale = d3
    .scaleLinear()
    .domain([d3.min(data.flat()), d3.max(data.flat())])
    .range(["#FCE4E4", baseColor]);

    // Tooltip setup
    // const tooltip = d3
    //   .select(ref.current)
    //   .append("div")
    //   .style("position", "absolute")
    //   .style("background", "rgba(0,0,0,0.7)")
    //   .style("color", "white")
    //   .style("padding", "5px")
    //   .style("border-radius", "5px")
    //   .style("opacity", 0)
    //   .style("pointer-events", "none");

    // Draw rectangles
    svg
      .selectAll()
      .data(data.flat())
      .enter()
      .append("rect")
      .attr("x", (_, i) => x(columnNames[i % columnNames.length]))
      .attr("y", (_, i) => y(rowNames[Math.floor(i / columnNames.length)]))
      .attr("width", x.bandwidth())
      .attr("height", y.bandwidth())
      .style("fill", (d) => colorScale(d))
      .style("stroke", showStroke ? "white" : "none") // Stroke toggle
      .attr("rx", 10) // Add this for curved edges
      .attr("ry", 10) // Add this for curved edges
      // .on("mouseover", function (event, d) {
        // const columnIndex = event.target.x.baseVal.value / x.bandwidth();
        // const rowIndex = event.target.y.baseVal.value / y.bandwidth();
        // tooltip
        //   .style("opacity", 1)
        //   .html(
        //     `Row: ${rowNames[Math.floor(rowIndex)]}<br>Column: ${
        //       columnNames[Math.floor(columnIndex)]
        //     }<br>Value: ${d}`
        //   );
      // })
      // .on("mousemove", function (event) {
      //   tooltip
      //     .style("top", `${event.pageY - 30}px`)
      //     .style("left", `${event.pageX + 10}px`);
      // })
      // .on("mouseout", function () {
      //   tooltip.style("opacity", 0);
      // });

    // Add text inside rectangles
    if (showValues) {
      svg
        .selectAll()
        .data(data.flat())
        .enter()
        .append("text")
        .attr(
          "x",
          (_, i) => x(columnNames[i % columnNames.length]) + x.bandwidth() / 2
        )
        .attr(
          "y",
          (_, i) =>
            y(rowNames[Math.floor(i / columnNames.length)]) + y.bandwidth() / 2
        )
        .text((d) => d)
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "middle")
        .style("fill", fontColor)
        .style("font-size", `${fontSize}px`);
    }

    // Add x-axis
    const xAxis = svg
      .append("g")
      .attr("transform", `translate(25, ${innerHeight})`)
      .call(d3.axisBottom(x));

    if (!showXAxisLine) {
      xAxis.selectAll("line").remove();
      xAxis.selectAll("path").remove();
    } 
    const tooltip = d3.select("body")
    .append("div")
    .style("position", "absolute")
    .style("visibility", "hidden")
    .style("background", "#fff")
    .style("border", "1px solid #ccc")
    .style("padding", "5px")
    .style("border-radius", "4px")
    .style("font-size", "12px")
    .style("box-shadow", "0px 0px 5px rgba(0,0,0,0.3)");
    xAxis
      .selectAll("text")
      .text(d => d.length > 10 ? d.substring(0, 10) + "..." : d)
      // .attr("title", d => d)
      .style("font-size", `${fontSize}px`)
      .style("fill", fontColor)
      .attr("transform", "rotate(0)")
      .attr("text-anchor", "end")
      .attr("x", +20)
      .attr("y", +5)
      .on("mouseover", function (event, d) {
        tooltip.text(d)
          .style("visibility", "visible")
          .style("top", (event.pageY - 10) + "px")
          .style("left", (event.pageX + 10) + "px");
      })
      .on("mousemove", function (event) {
        tooltip.style("top", (event.pageY - 10) + "px")
          .style("left", (event.pageX + 10) + "px");
      })
      .on("mouseout", function () {
        tooltip.style("visibility", "hidden");
      });

    // Add y-axis
    const yAxis = svg.append("g").call(d3.axisLeft(y));

    if (!showYAxisLine) {
      yAxis.selectAll("line").remove();
      yAxis.selectAll("path").remove();
    }

    yAxis
      .selectAll("text")
      .style("font-size", `${fontSize}px`)
      .style("fill", fontColor);

    // Add color scale legend
    // const legendWidth = legendPosition === "bottom" ? innerWidth : 20;
    // const legendHeight = legendPosition === "bottom" ? 8 : innerHeight;
    // const legendSvg = svg
    //   .append("g")
    //   .attr(
    //     "transform",
    //     legendPosition === "bottom"
    //       ? `translate(0, ${innerHeight + 40})`
    //       : `translate(${innerWidth + 20}, 0)`
    //   );

    // const legendScale = d3
    //   .scaleLinear()
    //   .range([0, legendPosition === "bottom" ? legendWidth : legendHeight])
    //   .domain([d3.min(data.flat()), d3.max(data.flat())]);

    // const legendAxis =
    //   legendPosition === "bottom"
    //     ? d3.axisBottom(legendScale).ticks(5)
    //     : d3.axisRight(legendScale).ticks(5);

    // if (legendPosition === "bottom") {
    //   // Horizontal legend
    //   legendSvg
    //     .selectAll("rect")
    //     .data(d3.range(0, 1, 0.01))
    //     .enter()
    //     .append("rect")
    //     .attr("x", (d) => d * legendWidth)
    //     .attr("y", 0)
    //     .attr("width", legendWidth / 100)
    //     .attr("height", legendHeight)
    //     .style("fill", (d) =>
    //       colorScale(
    //         d * (d3.max(data.flat()) - d3.min(data.flat())) +
    //           d3.min(data.flat())
    //       )
    //     );

    //   legendSvg.append("g").call(legendAxis);
    // } else {
    //   // Vertical legend
    //   legendSvg
    //     .selectAll("rect")
    //     .data(d3.range(0, 1, 0.01))
    //     .enter()
    //     .append("rect")
    //     .attr("x", 0)
    //     .attr("y", (d) => d * legendHeight)
    //     .attr("width", legendWidth)
    //     .attr("height", legendHeight / 100)
    //     .style("fill", (d) =>
    //       colorScale(
    //         d * (d3.max(data.flat()) - d3.min(data.flat())) +
    //           d3.min(data.flat())
    //       )
    //     );

    //   legendSvg.append("g").call(legendAxis);
    // }

    // Add color scale legend
    const legendSvg = svg
      .append("g")
      .attr("transform", `translate(0, ${innerHeight + 40})`);

    const legendWidth = innerWidth;
    const gradientId = "heatmap-gradient";

    // Define a gradient for the legend
    svg
      .append("defs")
      .append("linearGradient")
      .attr("id", gradientId)
      .attr("x1", "0%")
      .attr("x2", "100%")
      .attr("y1", "0%")
      .attr("y2", "0%")
      .selectAll("stop")
      .data(
        d3.range(0, 1.01, 0.1).map((t) => ({
          offset: `${t * 100}%`,
          color: colorScale(
            t * (d3.max(data.flat()) - d3.min(data.flat())) +
              d3.min(data.flat())
          ),
        }))
      )
      .enter()
      .append("stop")
      .attr("offset", (d) => d.offset)
      .attr("stop-color", (d) => d.color);

    // Add the gradient bar
    legendSvg
    .append("rect")
    .attr("x", 0)
    .attr("y", 0)
    .attr("width", legendWidth)
    .attr("height", 6)
    .attr("rx", 5) // Horizontal radius for curved edges
    .attr("ry", 5) // Vertical radius for curved edges
    .style("fill", `url(#${gradientId})`);

    // Add labels
    const legendScale = d3
      .scaleLinear()
      .range([0, legendWidth])
      .domain([d3.min(data.flat()), d3.max(data.flat())]);

    const legendAxis = d3
      .axisBottom(legendScale)
      .ticks(5)
      .tickSize(0)
      .tickFormat(d3.format(".1f")); // Adjust format as needed

    legendSvg
      .append("g")
      .attr("transform", `translate(0, 10)`)
      .call(legendAxis)
      .call((g) => g.select(".domain").remove()); // Remove axis line
  }, [
    data,
    rowNames,
    columnNames,
    showValues,
    colorScaleName,
    legendPosition,
    gapBetweenCells,
    showXAxisLine,
    showYAxisLine,
    showStroke,
    height,
    fontSize,
    fontColor,
    width,
    baseColor,
    overflow
  ]);

  return (
    <div>
      <div ref={ref} style={{ width, overflow }}></div>
    </div>
  );
};

export default Heatmap;
